import { DeviceSyncField } from '@am3nd-org/pointcraft-types';

import { useDeviceContextStore } from 'src/store/DeviceContext';

import { loadDeviceSession } from './auth-v3/session-store';
import { syncDeviceSyncV4Fields } from './device-sync-v4.service';

export type ApplicationContextRecoveryResult = 'not-required' | 'recovered' | 'pending';

export const APPLICATION_CONTEXT_RECOVERY_KEY = 'pointcraft-application-context-recovery';

export function markApplicationContextRecoveryRequired(): void {
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(APPLICATION_CONTEXT_RECOVERY_KEY, 'required');
  }
}

export function clearApplicationContextRecoveryRequired(): void {
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(APPLICATION_CONTEXT_RECOVERY_KEY);
  }
}

export function isApplicationContextRecoveryRequired(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.sessionStorage.getItem(APPLICATION_CONTEXT_RECOVERY_KEY) === 'required'
  );
}

/**
 * Retries the fatal application-context bootstrap from the support route.
 * A missing context is deliberately left missing after failure so a page reload
 * (and the support page's retry timer) will make another network request.
 */
export async function recoverMissingApplicationContext(): Promise<ApplicationContextRecoveryResult> {
  const deviceSession = await loadDeviceSession();
  if (!deviceSession) {
    clearApplicationContextRecoveryRequired();
    return 'not-required';
  }

  const contextStore = useDeviceContextStore.getState();
  if (contextStore.getApplicationDeviceContext()) {
    if (!isApplicationContextRecoveryRequired()) return 'not-required';
    clearApplicationContextRecoveryRequired();
    return 'recovered';
  }

  markApplicationContextRecoveryRequired();
  contextStore.setStatus('resolving');

  try {
    await syncDeviceSyncV4Fields([DeviceSyncField.APPLICATION_DEVICE_CONTEXT]);

    if (!useDeviceContextStore.getState().getApplicationDeviceContext()) {
      throw new Error('Application context was not returned by device sync');
    }

    clearApplicationContextRecoveryRequired();
    useDeviceContextStore.getState().setStatus('ready');
    return 'recovered';
  } catch (error) {
    useDeviceContextStore
      .getState()
      .setStatus(
        'error',
        error instanceof Error ? error.message : 'Application context recovery failed'
      );
    return 'pending';
  }
}
